import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ElementContent } from "./ElementRenderer";

export function CanvasElement({
  element,
  isSelected,
  onClick,
  onDelete,
  onDuplicate,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: element.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`canvas-element-${element.id}`}
      className={`group relative ${
        isSelected
          ? "canvas-element-selected"
          : "canvas-element-hover"
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
    >
      {/* Drag handle */}
      <div
        className={`absolute -left-7 top-1/2 -translate-y-1/2 ${
          isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        } transition-opacity duration-150`}
      >
        <button
          className="p-0.5 rounded bg-gray-100 hover:bg-gray-200 cursor-grab active:cursor-grabbing border border-gray-200"
          {...attributes}
          {...listeners}
          data-testid={`drag-handle-${element.id}`}
        >
          <GripVertical className="w-3 h-3 text-gray-400" />
        </button>
      </div>

      {/* Action buttons */}
      {isSelected && (
        <div className="absolute -right-1 -top-3 flex gap-0.5 z-10">
          <Button
            size="icon"
            variant="outline"
            className="h-5 w-5 bg-white shadow-sm"
            data-testid={`duplicate-element-${element.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <Copy className="w-2.5 h-2.5" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            className="h-5 w-5 bg-white shadow-sm text-red-500 hover:text-red-600 hover:bg-red-50"
            data-testid={`delete-element-${element.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="w-2.5 h-2.5" />
          </Button>
        </div>
      )}

      {/* Element content */}
      <ElementContent element={element} />

      {/* Type label */}
      <div
        className={`absolute -left-7 top-0 -translate-x-full ${
          isSelected ? "opacity-100" : "opacity-0"
        } transition-opacity duration-150`}
      >
        <span className="text-[9px] font-mono text-blue-500 whitespace-nowrap bg-blue-50 px-1 py-0.5 rounded border border-blue-100">
          {element.label}
        </span>
      </div>
    </div>
  );
}
